import { useCallback, useEffect } from 'react';
import { useQuery } from 'data-fetching-client';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModal from 'UIComponents/dialog/UIModal';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { H2 } from 'UIComponents/elements/headings';
import UILink from 'UIComponents/link/UILink';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { useCleanupScopedLocalStorage } from 'growth-payments-core/localStorage/useLocalStorageState';
import TrackRender from 'growth-payments-core/usage-tracker/TrackRender';
import { events } from 'growth-payments-core/usage-tracker/events';
import { getParentWindow } from 'growth-payments-core/query-string/utils';
import { useQueryStringState } from 'growth-payments-core/query-string/useQueryStringState';
import { navigateTo } from 'growth-payments-core/urls/utils';
import { getCommerceHomeUrl } from 'growth-payments-core/urls/urls';
import { GET_PAYMENTS_ELIGIBILITY } from '../../queries/paymentAccountQueries';
import useRefetchAfterOAuthOnboardingCompletion from '../../hooks/useRefetchAfterOAuthOnboardingCompletion';
import useOAuthSessionLatestStatus from '../../hooks/useOAuthSessionLatestStatus';
import { markSetUpPaymentsComplete } from '../../utils/tasks';
import CelebrationModal from './CelebrationModal';
import { EnrollmentFlowType } from '../../types';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const TEST_IDS = {
  OAUTH_ACCOUNT_ONBOARDING_MODAL: 'oauth-account-onboarding-modal',
  OAUTH_ACCOUNT_ONBOARDING_MODAL_CTA: 'oauth-account-onboarding-modal-cta',
  OAUTH_ACCOUNT_ONBOARDING_MODAL_LOADED: 'oauth-account-onboarding-modal-loaded'
};
const OauthAccountOnboardingModalLoadingContents = () => {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UIDialogHeader, {
      children: /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "OauthAccountOnboardingModal.loading"
        })
      })
    }), /*#__PURE__*/_jsx(UIDialogBody, {
      style: {
        position: 'relative'
      },
      children: /*#__PURE__*/_jsx(UILoadingSpinner, {
        className: "m-bottom-6",
        layout: "centered",
        size: "small"
      })
    })]
  });
};
const OauthAccountOnboardingModalLoadedContents = () => {
  // Cache is already primed with updated eligibility after a successful OAuth connection by the refetch call in useOAuthSessionLatestStatus
  const {
    data: eligibilityData
  } = useQuery(GET_PAYMENTS_ELIGIBILITY, {
    fetchPolicy: 'cache-only'
  });
  const [__, setCurrentEnrollmentFlow] = useQueryStringState('paymentsEnrollment', EnrollmentFlowType.processorPicker);
  const cleanupScopedLocalStorage = useCleanupScopedLocalStorage(['kyc-step-stack', 'hs-payments-kyc']);
  useEffect(() => {
    cleanupScopedLocalStorage();
  }, [cleanupScopedLocalStorage]);
  const {
    refetch: refetchAndUpdateThePaymentSettingsCache,
    loading: loadingRefetch
  } = useRefetchAfterOAuthOnboardingCompletion();
  const handleClick = useCallback(() => {
    if (getParentWindow().location.href.includes('/commerce/')) {
      refetchAndUpdateThePaymentSettingsCache();
      navigateTo(getCommerceHomeUrl());
    } else {
      markSetUpPaymentsComplete().catch(() => {});
      setCurrentEnrollmentFlow(undefined);
      getParentWindow().location.reload();
    }
  }, [refetchAndUpdateThePaymentSettingsCache, setCurrentEnrollmentFlow]);
  return /*#__PURE__*/_jsx(CelebrationModal, {
    title: "OauthAccountOnboardingModal.header",
    testID: TEST_IDS.OAUTH_ACCOUNT_ONBOARDING_MODAL_LOADED,
    bodyContent: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(TrackRender, {
        event: events.growthPaymentsPageView,
        properties: {
          screen: 'stripe-oauth-success-modal',
          chargesEnabled: eligibilityData.paymentsEnrollmentEmbedEligibility.chargesEnabled,
          payoutsEnabled: eligibilityData.paymentsEnrollmentEmbedEligibility.payoutsEnabled
        }
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: eligibilityData.paymentsEnrollmentEmbedEligibility.chargesEnabled ? 'OauthAccountOnboardingModal.chargesEnabledBody' : 'OauthAccountOnboardingModal.body'
      })]
    }),
    onClose: handleClick,
    CTA: /*#__PURE__*/_jsx(UILoadingButton, {
      "data-test-id": TEST_IDS.OAUTH_ACCOUNT_ONBOARDING_MODAL_CTA,
      use: "primary",
      onClick: handleClick,
      loading: loadingRefetch
      // Don't show the result icon, since this modal will unmount on API success or failure
      ,
      failed: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "OauthAccountOnboardingModal.primaryCta"
      })
    })
  });
};

/*
Onboarding modal for OAuth.

On mount, this component polls the /latest-status-by-portal API to see if an OAuth account is connected.
A loading state is shown to the user while the polling takes place until the latest-status is "SUCCESSFULLY_CONNECTED" or there is an error.
*/
const OauthAccountOnboardingModal = ({
  onClose
}) => {
  const {
    getOAuthSessionLatestStatus,
    loading
  } = useOAuthSessionLatestStatus({
    onErrorHandler: e => {
      if ((e === null || e === void 0 ? void 0 : e.message) === 'connected-account-polling-failed') {
        FloatingAlertStore.addAlert({
          id: 'connected-account-polling-failed',
          message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "OauthAccountOnboardingModal.errorAlert.connectedAccountNotFound.message_jsx",
            options: {
              href: window.location.href
            },
            elements: {
              Link: UILink
            }
          }),
          type: 'danger'
        });
      }
      onClose();
    }
  });
  useEffect(() => {
    getOAuthSessionLatestStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/_jsx(UIModal, {
    centered: true,
    use: "success",
    width: 550,
    "data-test-id": TEST_IDS.OAUTH_ACCOUNT_ONBOARDING_MODAL,
    children: loading ? /*#__PURE__*/_jsx(OauthAccountOnboardingModalLoadingContents, {}) : /*#__PURE__*/_jsx(OauthAccountOnboardingModalLoadedContents, {})
  });
};
export default OauthAccountOnboardingModal;